// Ask the browser to keep this origin's locally-stored data out of its automatic
// eviction pool. Web recordings that have not yet uploaded live in IndexedDB
// (useIndexDb.js); under storage pressure a browser can SILENTLY evict that data
// (Safari is the most aggressive), which would drop a not-yet-synced recording.
// A granted persistent-storage request exempts the origin from that eviction.
//
// Best-effort and idempotent by design:
//   - feature-guarded — no-ops on browsers without the Storage Manager API and in
//     non-browser (SSR/test) environments, so it never assumes the API exists;
//   - never throws into its caller — a storage-permission failure must not break
//     app/recorder init;
//   - logs only the grant STATUS (a boolean), never any recording content, so it
//     is PHI-safe.
//
// Returns the resulting persisted() state (true = exempt from eviction).
export async function requestPersistentStorage() {
  try {
    if (typeof navigator === "undefined" || !navigator.storage || !navigator.storage.persist) {
      // Unsupported browser (older Safari, some privacy modes) or SSR/test: no-op.
      return false;
    }

    // If we're already exempt, don't re-prompt the browser — just confirm.
    if (await navigator.storage.persisted()) {
      console.log("Persistent storage already granted");
      return true;
    }

    await navigator.storage.persist();
    // Verify the outcome by reading the durable state back, rather than trusting
    // the request's return value — the two can diverge across engines, and this
    // is the state the eviction policy actually keys on.
    const persisted = await navigator.storage.persisted();
    console.log(`Persistent storage request: ${persisted ? "granted" : "denied"}`);
    return persisted;
  } catch (e) {
    // Never let a storage-permission failure break the caller's init path.
    console.warn("Persistent storage request failed", e?.name || e);
    return false;
  }
}
